/**
 * "Edit chatter" — alle per-chatter velden en rechten in één dialoog.
 *
 * De bundle stuurt bij opslaan één PATCH /api/admin/models/chatters/{id} met
 * naam, telegram_chat_id en alle allow_*-vlaggen in de body. Hier gebeurt
 * precies hetzelfde, met dezelfde veldnamen.
 *
 * Dezelfde dialoog doet ook "Create chatter account" (chatter.id === 0). De
 * backend splitst dat: POST /chatters neemt alleen naam + telegram-id's aan,
 * de rechten gaan daarna in een PATCH op het verse id.
 */
import { useState } from 'react'
import { Modal, Field } from './Modal'
import { useActions } from '../lib/actions'
import { api } from '../lib/api'
import { RechtenBlok } from './RechtenBlok'
import { tokensUitChatter, vlaggenUitTokens } from '../lib/rechtenBlok'
import { ModelKeuze, type ModelOptie } from './ModelKeuze'
import { ZoneKeuze } from './ZoneKeuze'
import { TELEGRAM_ID_HINT, telegramIdFout } from '../lib/telegramId'

export interface ChatterRights {
  id: number
  name: string
  telegram_chat_id: string | null
  telegram_user_id?: string | null
  assigned_models?: { model_persona_id: number }[] | null
  allow_mass_dm: boolean
  allow_native_mass_dm: boolean
  allow_paid_mass_dm: boolean
  allow_private_self_claim: boolean
  allow_private_manage_all: boolean
  allow_timewaster_mark: boolean
  allow_vip_mark: boolean
  allow_toggle_ai: boolean
  allow_followers: boolean
  hide_earnings: boolean
  view_all_unlocks: boolean
  allow_chatter_dashboard: boolean
  allow_chat_blocking: boolean
  allow_create_chatter_accounts: boolean
  mass_dm_filter_grants?: unknown
  /** 05-10-2026: paginarechten van de login. */
  page_tokens?: string[]
  has_login?: boolean
  is_lead?: boolean
  /** 24-10-2026 (Jordy): head chatter-instellingen, alleen voor de owner. */
  lead_override_pct?: number | null
  lead_chatter_id?: number | null
  /** Sinds wanneer de huidige koppeling telt (UTC, Z); alleen voor de owner. */
  referral_since?: string | null
  /** 07-10-2026: tijdzone-voorkeur van de login (IANA); null = Amsterdam. */
  timezone?: string | null
}

// 05-10-2026: de rechtenlijst staat in lib/rechtenBlok.ts (gedeeld met Settings → Users).

/** UTC-moment leesbaar, met de tijdzone erbij: "24 Oct 2026, 19:52 UTC". */
export function utcLabel(iso: string | null | undefined): string {
  if (!iso) return '—'
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(t.getTime())) return '—'
  return `${t.toLocaleString('en-GB', {
    timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })} UTC`
}

export function ChatterEditDialog({
  isOwner = false,
  isAdmin = false,
  chatter,
  personas = [],
  heads = [],
  onClose,
  onSaved,
}: {
  isOwner?: boolean
  /** 24-10-2026: de head chatters om uit te kiezen (Reports to head chatter). */
  heads?: { id: number; name: string; lead_override_pct?: number | null; lead_chatter_id?: number | null }[]
  /** 07-10-2026 (Jordy): een admin zet alleen de schuif "Mass Messages page". */
  isAdmin?: boolean
  chatter: ChatterRights
  /** Alle modellen, om toe te wijzen (30-09: ontbrak in Edit). */
  personas?: ModelOptie[]
  onClose: () => void
  onSaved: () => void
}) {
  const { run, busy } = useActions()
  const [name, setName] = useState(chatter.name ?? '')
  const [tg, setTg] = useState(chatter.telegram_chat_id ?? '')
  const [tgUser, setTgUser] = useState(chatter.telegram_user_id ?? '')
  const [modellen, setModellen] = useState<number[]>(
    () => (chatter.assigned_models ?? []).map((m) => m.model_persona_id))
  // 05-10-2026 (Jordy): de rechten als tokenlijst, in het gedeelde blok
  // (zelfde als Settings → Users). Alleen een owner wijzigt ze.
  const [pages, setPages] = useState<string[]>(
    () => tokensUitChatter(chatter as unknown as Record<string, unknown>))
  const metLogin = chatter.has_login !== false
  // 07-10-2026 (Jordy): tijdzone voor Mass Messages; staat op de login.
  const [zone, setZone] = useState(chatter.timezone ?? '')

  const isNew = !chatter.id
  const label = isNew ? 'Create chatter account' : 'Edit chatter'

  // 24-10-2026 (Jordy): Head chatter, Head referral % en Reports to head
  // chatter — alleen de owner ziet en zet ze. Een nieuwe koppeling telt pas
  // vanaf het moment van opslaan (referral_since op de server).
  const headVelden = isOwner && !isNew
  const [isHead, setIsHead] = useState(Boolean(chatter.is_lead))
  const [headPct, setHeadPct] = useState(String(chatter.lead_override_pct ?? 0))
  const [reportsTo, setReportsTo] = useState(chatter.lead_chatter_id ? String(chatter.lead_chatter_id) : '')
  const [bevestig, setBevestig] = useState(false)
  const oudLead = chatter.lead_chatter_id ? Number(chatter.lead_chatter_id) : null
  const nieuwLead = reportsTo ? Number(reportsTo) : null
  const leadWissel = headVelden && oudLead !== nieuwLead
  const pctWissel = headVelden && Number(headPct) !== Number(chatter.lead_override_pct ?? 0)
  const headWissel = headVelden && isHead !== Boolean(chatter.is_lead)
  const naamVan = (id: number | null) => heads.find((h) => h.id === id)?.name ?? (id ? `#${id}` : '—')
  const pctVan = (id: number | null) => Number(heads.find((h) => h.id === id)?.lead_override_pct ?? 0)
  // Kring vooraf zichtbaar maken; de server controleert het ook.
  const kring = (() => {
    if (!headVelden || !nieuwLead) return false
    const gezien = new Set<number>([chatter.id])
    let h = heads.find((x) => x.id === nieuwLead)
    while (h && h.lead_chatter_id) {
      if (gezien.has(h.lead_chatter_id)) return true
      gezien.add(h.lead_chatter_id)
      h = heads.find((x) => x.id === h!.lead_chatter_id)
    }
    return false
  })()
  const headProbleem = !headVelden ? ''
    : headPct === '' || Number.isNaN(Number(headPct)) || Number(headPct) < 0 || Number(headPct) > 100
        || !Number.isInteger(Number(headPct))
      ? 'Head referral % must be a whole number between 0 and 100.'
      : nieuwLead === chatter.id ? 'A chatter cannot report to themselves.'
        : kring ? 'This would create a reporting loop between head chatters.'
          : ''

  // 2-11-2026 (Jordy): Telegram-id's alleen als getal; de server controleert het ook.
  const tgProbleem = telegramIdFout(tgUser, 'Telegram user ID', chatter.telegram_user_id)
    || telegramIdFout(tg, 'Telegram Chat ID', chatter.telegram_chat_id)

  const body = {
    name,
    telegram_chat_id: tg,
    telegram_user_id: tgUser,
    model_persona_ids: modellen,
    // Rechten (vlaggen, filters, pagina's) alleen als een owner opslaat en er
    // een login is; anderen sturen ze niet mee (de server weigert wijzigingen).
    ...(isOwner && metLogin ? vlaggenUitTokens(pages, Boolean(chatter.is_lead)) : {}),
    // 07-10-2026: geen recht, dus ook voor een manager; leeg = Amsterdam.
    ...(chatter.id && metLogin ? { timezone: zone } : {}),
    // 07-10-2026 (Jordy): een admin stuurt alleen de paginarechten mee; de
    // server laat hem daarin alleen "Mass Messages page" omzetten.
    ...(!isOwner && isAdmin && metLogin
      ? { page_tokens: vlaggenUitTokens(pages, Boolean(chatter.is_lead)).page_tokens }
      : {}),
    // 24-10-2026: alleen meesturen wat de owner echt wijzigt; 0 = loskoppelen.
    ...(headWissel ? { is_lead: isHead } : {}),
    ...(pctWissel ? { lead_override_pct: Number(headPct) } : {}),
    ...(leadWissel ? { lead_chatter_id: nieuwLead ?? 0 } : {}),
  }

  function opslaan() {
    run({
      label,
      request: save,
      onDone: () => { onSaved(); onClose() },
      successText: isNew ? 'Chatter created' : 'Chatter saved',
    })
  }

  /**
   * Bestaande chatter: één PATCH, zoals de oude bundle. Nieuwe chatter: eerst
   * POST (alleen naam + telegram) en dan de rechten in een PATCH op het id dat
   * terugkomt — de create-route accepteert de allow_*-velden niet.
   */
  async function save() {
    if (!isNew) return api.patch(`/api/admin/models/chatters/${chatter.id}`, body)
    const made = await api.post('/api/admin/models/chatters', {
      name,
      telegram_user_id: null,
      telegram_chat_id: tg || null,
    }) as { id?: number } | null
    if (!made?.id) return made
    return api.patch(`/api/admin/models/chatters/${made.id}`, body)
  }

  return (
    <Modal
      title={isNew ? 'Create chatter account' : `Edit chatter — ${chatter.name}`}
      desc={isNew
        ? "New chatter profile."
        : "Everything this chatter may do in the dashboard."}
      width={620}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn primary sm"
            type="button"
            disabled={!name.trim() || busy === label || Boolean(headProbleem) || Boolean(tgProbleem)}
            onClick={() => (leadWissel || pctWissel || headWissel ? setBevestig(true) : opslaan())}
          >
            Save
          </button>
        </>
      }
    >
      <Field label="Name">
        <input className="inp" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="Telegram User ID" hint={`${TELEGRAM_ID_HINT}. Leave empty if there is no Telegram link.`}>
          <input className="inp" inputMode="numeric" value={tgUser} onChange={(e) => setTgUser(e.target.value)} />
        </Field>
        <Field label="Telegram Chat ID" hint={TELEGRAM_ID_HINT}>
          <input className="inp" inputMode="numeric" value={tg} onChange={(e) => setTg(e.target.value)} />
        </Field>
      </div>
      {tgProbleem ? (
        <div className="mut2" style={{ fontSize: 12, color: '#fca5a5' }}>{tgProbleem}</div>
      ) : null}
      {chatter.id && metLogin ? (
        <Field label="Time zone"
               hint="Mass Messages: dates and times are entered and shown in this zone. Empty = Amsterdam.">
          <ZoneKeuze value={zone} onChange={setZone} leegLabel="Default (Europe/Amsterdam)" />
        </Field>
      ) : null}

      <div className="divh" />
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Permissions</div>
      {!isNew && chatter.has_login === false ? (
        <div className="mut2" style={{ fontSize: 12, color: '#fcd34d', marginBottom: 6 }}>
          This chatter has no login yet. Permissions are stored on the login, so they cannot be
          saved here. Create the account via "Create chatter account".
        </div>
      ) : null}
      {metLogin ? (
        <RechtenBlok rol="chatter" pages={pages} onChange={setPages} magWijzigen={isOwner}
                     adminMag={isAdmin} isLead={Boolean(chatter.is_lead)} />
      ) : null}

      {headVelden ? (
        <>
          <div className="divh" />
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Head chatter (owner only)</div>
          <label className="row" style={{ gap: 8, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={isHead} onChange={(e) => setIsHead(e.target.checked)} />
            Head chatter
          </label>
          <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Head referral %"
                   hint="What this head chatter earns on the Net of the sales of the chatters who report to them.">
              <input className="inp" inputMode="numeric" value={headPct} disabled={!isHead}
                     onChange={(e) => setHeadPct(e.target.value.trim())} />
            </Field>
            <Field label="Reports to head chatter"
                   hint={chatter.lead_chatter_id
                     ? `Referral since ${utcLabel(chatter.referral_since)}`
                     : 'Not linked to a head chatter.'}>
              <select className="inp" value={reportsTo} onChange={(e) => setReportsTo(e.target.value)}>
                <option value="">— None —</option>
                {heads.filter((h) => h.id !== chatter.id).map((h) => (
                  <option key={h.id} value={String(h.id)}>
                    {h.name} ({Number(h.lead_override_pct ?? 0)}%)
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {headProbleem ? (
            <div className="mut2" style={{ fontSize: 12, color: '#fca5a5' }}>{headProbleem}</div>
          ) : null}
        </>
      ) : null}

      {bevestig ? (
        <Modal
          title="Confirm head chatter change"
          width={520}
          onClose={() => setBevestig(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setBevestig(false)}>Cancel</button>
              <button className="btn primary sm" type="button" disabled={busy === label}
                      onClick={() => { setBevestig(false); opslaan() }}>
                Confirm and save
              </button>
            </>
          }
        >
          <div style={{ fontSize: 13, display: 'grid', gap: 8 }}>
            {leadWissel && nieuwLead ? (
              <p style={{ margin: 0 }}>
                <b>{chatter.name}</b> will report to <b>{naamVan(nieuwLead)}</b>. {naamVan(nieuwLead)} earns{' '}
                <b>{pctVan(nieuwLead)}%</b> referral on the Net of {chatter.name}'s sales.
                The referral only counts from now on: only sales made <b>after you save</b> count.
                Earlier sales never count for {naamVan(nieuwLead)}.
              </p>
            ) : null}
            {leadWissel && oudLead ? (
              <p style={{ margin: 0 }}>
                <b>{naamVan(oudLead)}</b> stops earning referral on {chatter.name}'s new sales. What{' '}
                {naamVan(oudLead)} already earned on sales before now stays with {naamVan(oudLead)}.
              </p>
            ) : null}
            {leadWissel && !nieuwLead ? (
              <p style={{ margin: 0 }}>{chatter.name} will no longer report to a head chatter.</p>
            ) : null}
            {headWissel && isHead ? (
              <p style={{ margin: 0 }}>
                <b>{chatter.name}</b> becomes a head chatter with <b>{Number(headPct)}%</b> referral. Only sales
                made <b>after you save</b> count; earlier sales never count for this new period.
              </p>
            ) : null}
            {headWissel && !isHead ? (
              <p style={{ margin: 0 }}>
                <b>{chatter.name}</b> is no longer a head chatter. From the moment you save, {chatter.name} earns
                no referral on new sales. What {chatter.name} already earned on sales before now stays.
                Chatters who report to {chatter.name} stay linked, but their new sales earn {chatter.name} nothing
                unless {chatter.name} becomes a head chatter again.
              </p>
            ) : null}
            {pctWissel && isHead ? (
              <p style={{ margin: 0 }}>
                Head referral % of {chatter.name}: <b>{Number(chatter.lead_override_pct ?? 0)}%</b> →{' '}
                <b>{Number(headPct)}%</b>. The new percentage only applies to sales made <b>after you save</b>.
                Earlier sales keep {Number(chatter.lead_override_pct ?? 0)}%.
              </p>
            ) : null}
            <p className="mut2" style={{ margin: 0, fontSize: 12 }}>
              Nothing is paid out by saving. The change is written to the audit log (who, when, old and new).
            </p>
          </div>
        </Modal>
      ) : null}

      {isNew ? null : (
        <>
          <div className="divh" />
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Assigned models</div>
          <ModelKeuze modellen={personas} gekozen={modellen} onChange={setModellen} />
        </>
      )}
    </Modal>
  )
}
